import { useCallback, useEffect, useState } from "react";

export type MicPermissionError = "denied" | "unsupported";

/**
 * Owns the lifetime of the microphone MediaStream independently of any single
 * recording. In hands-free mode the same stream stays open across many
 * speech-detect → record → send cycles instead of re-prompting for mic
 * access every turn; in tap-to-talk mode it is acquired and released once
 * per turn, same as before.
 */
export function useMicStream() {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [permissionError, setPermissionError] = useState<MicPermissionError | null>(null);

  const ensure = useCallback(async (): Promise<MediaStream> => {
    if (stream) return stream;

    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setPermissionError("unsupported");
      throw new Error("Nahrávanie zvuku nie je v tomto prehliadači podporované.");
    }

    try {
      const nextStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      setPermissionError(null);
      setStream(nextStream);
      return nextStream;
    } catch {
      setPermissionError("denied");
      throw new Error("Prístup k mikrofónu bol zamietnutý.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream]);

  const release = useCallback(() => {
    setStream((current) => {
      current?.getTracks().forEach((track) => track.stop());
      return null;
    });
  }, []);

  useEffect(() => release, [release]);

  return { stream, ensure, release, permissionError };
}
